/**
 * Render-app geometry cache client (render micro-frontend only).
 *
 * Identical WorkerInput → identical WorkerOutput, so a device is built once and
 * every later thumbnail / render item / retry reuses it. Keyed by a version-salted
 * SHA-256 of the canonicalised input. All I/O is best-effort: any failure logs and
 * the caller falls back to building the geometry normally.
 */
import {stableStringify} from '../../utils/stableStringify'
import type {WorkerInput, WorkerOutput} from './deviceGeometryWorker'
import {encodeWorkerOutput, decodeWorkerOutput} from '../workerGeo/geometryCacheCodec'
import {GEOMETRY_CACHE_VERSION} from '../workerGeo/geometryCacheVersion'

const CACHE_BASE = '/api/renders/internal/geometry-cache'

/** Version-salted content hash of a worker build request. */
export const computeGeometryHash = async (cfg: WorkerInput): Promise<string> => {
  // stableStringify rounds floats (kills dimension drift) but does NOT sort keys,
  // so this relies on WorkerInput being built in a consistent key order — true
  // today (the single code path is DeviceMeshV2's workerConfig useMemo). The
  // version salt invalidates every key when the geometry logic changes.
  const serialized = `v${GEOMETRY_CACHE_VERSION}:${stableStringify(cfg)}`
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(serialized))
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** GET a cached build; null on miss / any error (caller then builds normally). */
export const fetchGeometryCache = async (hash: string): Promise<WorkerOutput | null> => {
  try {
    const resp = await fetch(`${CACHE_BASE}/${hash}`)
    if (!resp.ok) return null
    return decodeWorkerOutput(await resp.arrayBuffer())
  } catch {
    return null
  }
}

/** PUT a fresh build — fire-and-forget; never awaited, never blocks readiness. */
export const storeGeometryCache = (hash: string, output: WorkerOutput): void => {
  try {
    const body = encodeWorkerOutput(output)
    void fetch(`${CACHE_BASE}/${hash}`, {
      method: 'PUT',
      headers: {'Content-Type': 'application/octet-stream'},
      body,
    }).catch(() => {})
  } catch {
    // Encoding failed — non-fatal, the geometry has already been delivered.
  }
}
